import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import yup from 'src/utils/yup';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

import { updateClient } from 'src/module/authentication/action';
import { getClient } from 'src/module/authentication/selector';
import SwitchField from 'src/component/UI/SettingsModal/SettingsModalFields/SwitchField';
import { Map } from 'immutable';
import { InputLabel, Tooltip } from '@mui/material';
import HelpCircleIcon from '@mui/icons-material/HelpOutline';
import { fetchIntegrations } from 'src/module/integration/action';
import { useEmail, useSMS } from 'src/utils/utils';
import { useTranslation } from 'src/i18n';

export default function ClientPropertiesForm () {
  const { t } = useTranslation();
  const client: any = useSelector(getClient);
  const dispatch = useDispatch();
  const [textMessageMFA, setTextMessageMFA] = useState(client.getIn(['preferences', 'MFAOptions', 'text'], false));
  const [emailMFA, setEmailMFA] = useState(client.getIn(['preferences', 'MFAOptions', 'email'], false));
  const [OTPMFA, setOTPMFA] = useState(client.getIn(['preferences', 'MFAOptions', 'OTPMFA'], false));

  const [useEmailValue, setUseEmailValue] = useState<boolean | null>(null);
  const [useEmailLoading, setUseEmailLoading] = useState(true);
  const [useSMSValue, setUseSMSValue] = useState<boolean | null>(null);
  const [useSMSLoading, setUseSMSLoading] = useState(true);

  useEffect(() => {
    dispatch(fetchIntegrations());
  }, []);

  useEffect(() => {
    async function loadData () {
      const [emailResult, smsResult] = await Promise.all([useEmail(), useSMS()]);
      setUseEmailValue(emailResult);
      setUseEmailLoading(false);
      setUseSMSValue(smsResult);
      setUseSMSLoading(false);
    }
    loadData();
  }, []);
  const formik = useFormik({
    initialValues: {
      reportEmailSender: client.getIn(['preferences', 'reportEmailSender'], ''),
      name: client.get('name'),
      averageHoursPerWalkdown: client.getIn(['preferences', 'averageHoursPerWalkdown'], 1) || 1,
      averageHoursPerWorkOrder: client.getIn(['preferences', 'averageHoursPerWorkOrder'], 4) || 4,
      sessionTimeout: client.getIn(['preferences', 'sessionTimeout'], 0) || 0,
      passwordTimeout: client.getIn(['preferences', 'passwordTimeout'], 0) || 0,
    },
    onSubmit: values => {
      const { name, ...rest } = values;
      (rest as any).MFA = textMessageMFA || emailMFA || OTPMFA;
      (rest as any).MFAOptions = {};
      (rest as any).MFAOptions.text = textMessageMFA;
      (rest as any).MFAOptions.email = emailMFA;
      (rest as any).MFAOptions.OTPMFA = OTPMFA;
      dispatch(updateClient(name, Map(rest)));
    },
    validationSchema: () => {
      return yup.object().shape({
        name: yup
          .string(t('clientProperties.facilityName'))
          .required(t('clientProperties.facilityNameRequired')),
        reportEmailSender: yup
          .string(t('clientProperties.reportSenderEmail')),
        averageHoursPerWalkdown: yup
          .number(t('clientProperties.avgHoursWalkdown')),
        averageHoursPerWorkOrder: yup
          .number(t('clientProperties.avgHoursWorkOrder')),
        sessionTimeout: yup
          .number(t('clientProperties.sessionExpiry')),
        passwordTimeout: yup
          .number(t('clientProperties.passwordExpiry'))
      });
    },
  });

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    formik.validateForm().then(() => {
      formik.submitForm();
    });
  };

  return (
    <div className='client-properties-form'>
      <form onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          id="name"
          name="name"
          label={t('clientProperties.facilityName')}
          type="text"
          value={formik.values.name}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.name)}
          helperText={formik.errors.name as string}
          variant='outlined'
          InputLabelProps={{ shrink: false }}
          onBlur={handleBlur}
        />

        {!useEmailLoading && useEmailValue && <TextField
          fullWidth
          id="reportEmailSender"
          name="reportEmailSender"
          label={t('clientProperties.reportSenderEmail')}
          value={formik.values.reportEmailSender}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.reportEmailSender)}
          helperText={formik.errors.reportEmailSender as string}
          variant='outlined'
          InputLabelProps={{ shrink: false }}
          onBlur={handleBlur}
        />}

        <TextField
          fullWidth
          id="averageHoursPerWalkdown"
          name="averageHoursPerWalkdown"
          label={t('clientProperties.avgHoursWalkdown')}
          type="number"
          value={formik.values.averageHoursPerWalkdown}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.averageHoursPerWalkdown)}
          helperText={formik.errors.averageHoursPerWalkdown as string}
          variant='outlined'
          InputLabelProps={{ shrink: false }}
          onBlur={handleBlur}
        />

        <TextField
          fullWidth
          id="averageHoursPerWorkOrder"
          name="averageHoursPerWorkOrder"
          label={t('clientProperties.avgHoursWorkOrder')}
          type="number"
          value={formik.values.averageHoursPerWorkOrder}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.averageHoursPerWorkOrder)}
          helperText={formik.errors.averageHoursPerWorkOrder as string}
          variant='outlined'
          InputLabelProps={{ shrink: false }}
          onBlur={handleBlur}
        />

        <TextField
          fullWidth
          id="sessionTimeout"
          name="sessionTimeout"
          label={
            <div>
              <InputLabel className='input-label'>{t('clientProperties.sessionExpiry')}</InputLabel>
              <Tooltip title={t('clientProperties.sessionExpiryTooltip')} className='input-tooltip' enterTouchDelay={0}>
                <HelpCircleIcon className='help-icon' style={{ fontSize: 16 }} />
              </Tooltip>
            </div>
          }
          type="number"
          value={formik.values.sessionTimeout}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.sessionTimeout)}
          helperText={formik.errors.sessionTimeout as string}
          variant='outlined'
          InputLabelProps={{ shrink: false, style: { pointerEvents: "auto" } }}
          onBlur={handleBlur}
        />

        <TextField
          fullWidth
          id="passwordTimeout"
          name="passwordTimeout"
          label={
            <div>
              <InputLabel className='input-label'>{t('clientProperties.passwordExpiry')}</InputLabel>
              <Tooltip title={t('clientProperties.passwordExpiryTooltip')} className='input-tooltip' enterTouchDelay={0}>
                <HelpCircleIcon className='help-icon' style={{ fontSize: 16 }} />
              </Tooltip>
            </div>
          }
          type="number"
          value={formik.values.passwordTimeout}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.passwordTimeout)}
          helperText={formik.errors.passwordTimeout as string}
          onBlur={handleBlur}
          variant='outlined'
          InputLabelProps={{ shrink: false, style: { pointerEvents: "auto" } }}
        />

        <InputLabel className='mfa-label'>{t('clientProperties.mfaOptions')}</InputLabel>
        {!useSMSLoading && useSMSValue && (
          <div className='mfa-switch'><SwitchField value={textMessageMFA} field={Map({ label: t('clientProperties.textMessage'), id: null })} handleChange={() => { setTextMessageMFA(!textMessageMFA); formik.submitForm(); }} /></div>
        )}
        {!useEmailLoading && useEmailValue && (
          <div className='mfa-switch'><SwitchField value={emailMFA} field={Map({ label: t('auth.email'), id: null })} handleChange={() => { setEmailMFA(!emailMFA); formik.submitForm(); }} /></div>
        )}
        <div className='mfa-switch'><SwitchField value={OTPMFA} field={Map({ label: t('clientProperties.oneTimePassword'), id: null })} handleChange={() => { setOTPMFA(!OTPMFA); formik.submitForm(); }} /></div>

      </form>
    </div>
  );
}
